import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    PlusIcon,
    ClipboardDocumentListIcon,
    CheckCircleIcon,
    PaperClipIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/buttons/Button';
import { ExpandableText } from '../../../components/expandableText/ExpandableText';
import { fetchCourseTasks } from '../../../redux/actions/tasks';
import { formatDateTime } from '../../../common/formatDateText';
import { capitalizarPalabras } from '../../../common/upperCaseWord';
import { TaskFormDrawer } from './TaskFormDrawer';
import { TaskDetailDrawer } from './TaskDetailDrawer';
import { TaskFilterTabs } from './TaskFilterTabs';
import {
    TASK_PRIORITY_LABELS,
    TASK_WINDOW_LABELS,
    priorityBadgeClass,
    shouldShowTeacherTaskStatusBadge,
    statusBadgeClass,
    windowBadgeClass,
    teacherWindowLabel,
    teacherWindowBadgeClass,
    teacherDisplayName,
    canTeacherEditTask,
} from './taskLabels';
import './tasks-panel.css';

const TEACHER_FILTERS = [
    { id: 'all', label: 'Todas' },
    { id: 'upcoming', label: 'Por activar' },
    { id: 'to_review', label: 'Revisar' },
    { id: 'reviewed', label: 'Revisadas' },
];

const STUDENT_FILTERS = [
    { id: 'pending', label: 'Pendientes' },
    { id: 'submitted', label: 'Entregadas' },
    { id: 'expired', label: 'Vencidas' },
    { id: 'all', label: 'Todas' },
];

const isTeacherViewRole = (role) => role === 'teacher' || role === 'observer';

const pendingReviewCount = (task) => (
    Math.max(0, (task.submittedCount || 0) - (task.gradedCount || 0))
);

const matchesFilter = (task, filter, role) => {
    if (filter === 'all') return true;

    if (isTeacherViewRole(role)) {
        if (filter === 'upcoming') return task.windowState === 'upcoming';
        if (filter === 'to_review') return pendingReviewCount(task) > 0;
        if (filter === 'reviewed') {
            return (task.submittedCount || 0) > 0 && pendingReviewCount(task) === 0;
        }
        return true;
    }

    const submitted = Boolean(task.mySubmission);
    if (filter === 'pending') return !submitted && task.windowState === 'active';
    if (filter === 'submitted') return submitted;
    if (filter === 'expired') return !submitted && task.windowState === 'expired';
    return true;
};

const EMPTY_MESSAGES = {
    teacher: {
        all: 'Todavía no has creado tareas en este curso.',
        upcoming: 'No hay tareas pendientes de activación.',
        to_review: 'No hay entregas pendientes de revisar.',
        reviewed: 'Todavía no hay tareas revisadas.',
    },
    student: {
        all: 'Todavía no hay tareas disponibles en este curso.',
        pending: 'No tienes tareas pendientes. ¡Buen trabajo!',
        submitted: 'Todavía no has entregado ninguna tarea.',
        expired: 'No tienes tareas vencidas.',
    },
};

const TaskCard = ({ task, role, onOpen }) => {
    const submitted = Boolean(task.mySubmission);
    const isTeacherView = isTeacherViewRole(role);
    const progressPct = task.totalStudents
        ? Math.round((task.submittedCount / task.totalStudents) * 100)
        : 0;
    const toReview = pendingReviewCount(task);

    const handleKeyDown = (event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen();
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onOpen}
            onKeyDown={handleKeyDown}
            className="tasks-panel__card"
        >
            <div className="flex items-start justify-between gap-2">
                <h3 className="tasks-panel__card-title">
                    {capitalizarPalabras(task.title)}
                </h3>
                {task.attachment || task.attachments?.length ? (
                    <PaperClipIcon
                        className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                        title="Tiene archivo adjunto"
                    />
                ) : null}
            </div>

            <div className="tasks-panel__card-badges">
                <span className={isTeacherView
                    ? teacherWindowBadgeClass(task.windowState)
                    : windowBadgeClass(task.windowState)}
                >
                    {isTeacherView
                        ? teacherWindowLabel(task.windowState)
                        : TASK_WINDOW_LABELS[task.windowState]}
                </span>
                {isTeacherView && toReview > 0 && (
                    <span className={statusBadgeClass('in_progress')}>
                        {toReview === 1 ? '1 por revisar' : `${toReview} por revisar`}
                    </span>
                )}
                {isTeacherView && shouldShowTeacherTaskStatusBadge(task.status) && toReview === 0 && (
                    <span className={statusBadgeClass(task.status)}>
                        En revisión
                    </span>
                )}
                <span className={priorityBadgeClass(task.priority)}>
                    {TASK_PRIORITY_LABELS[task.priority]}
                </span>
                {role === 'student' && submitted && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-green-100 text-green-700">
                        <CheckCircleIcon className="w-3.5 h-3.5" aria-hidden="true" />
                        {Number.isInteger(task.mySubmission?.grade)
                            ? 'Calificada'
                            : 'Entregada'}
                    </span>
                )}
            </div>

            {task.description ? (
                <ExpandableText text={task.description} className="tasks-panel__card-desc" />
            ) : null}

            <dl className="tasks-panel__card-meta">
                <div className="tasks-panel__card-meta-row">
                    <dt>Fecha límite:</dt>
                    <dd>{formatDateTime(task.dueDate)}</dd>
                </div>
            </dl>

            {isTeacherView && (
                <div className="tasks-panel__card-progress">
                    <div className="tasks-panel__card-progress-head">
                        <span>Entregas</span>
                        <span className="tasks-panel__card-progress-count">
                            {task.submittedCount} / {task.totalStudents}
                        </span>
                    </div>
                    <div className="tasks-panel__card-progress-bar">
                        <div
                            className="tasks-panel__card-progress-fill"
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                    {(task.gradedCount || 0) > 0 ? (
                        <p className="tasks-panel__card-progress-note">
                            {task.gradedCount} calificada{(task.gradedCount || 0) === 1 ? '' : 's'}
                        </p>
                    ) : null}
                </div>
            )}

            {role === 'student' && task.teacherId && (
                <p className="tasks-panel__card-teacher">
                    Prof. {teacherDisplayName(task.teacherId)}
                </p>
            )}
        </div>
    );
};

export const TasksPanel = ({ courseId, initialTaskId = null }) => {
    const dispatch = useDispatch();
    const { loading, loaded, error, role, tasks } = useSelector((state) => state.tasks);

    const [filter, setFilter] = useState('all');
    const [formOpen, setFormOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [detailTaskId, setDetailTaskId] = useState(null);

    useEffect(() => {
        if (courseId) {
            dispatch(fetchCourseTasks(courseId));
        }
    }, [courseId, dispatch]);

    useEffect(() => {
        setFilter(role === 'student' ? 'pending' : 'all');
    }, [role]);

    useEffect(() => {
        if (initialTaskId) setDetailTaskId(initialTaskId);
    }, [initialTaskId]);

    const filters = isTeacherViewRole(role) ? TEACHER_FILTERS : STUDENT_FILTERS;

    const visibleTasks = useMemo(
        () => tasks
            .filter((task) => matchesFilter(task, filter, role))
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
        [tasks, filter, role],
    );

    const handleEdit = (task) => {
        if (!canTeacherEditTask(task)) {
            toast.error('No puedes editar una tarea que ya está activa o vencida.');
            return;
        }
        setDetailTaskId(null);
        setEditingTask(task);
        setFormOpen(true);
    };

    const closeForm = (options = {}) => {
        setFormOpen(false);
        if (options.reset) {
            setEditingTask(null);
        }
    };

    return (
        <div className="tasks-panel">
            <div className="tasks-panel__top">
                <div className="tasks-panel__heading">
                    <h2 className="tasks-panel__title">Tareas</h2>
                    {loaded ? (
                        <p className="tasks-panel__count" aria-live="polite">
                            {visibleTasks.length === 1
                                ? '1 tarea listada'
                                : `${visibleTasks.length} tareas listadas`}
                        </p>
                    ) : null}
                </div>

                {role === 'teacher' && (
                    <Button
                        variant="gradient"
                        fullWidth
                        className="tasks-panel__create"
                        icon={<PlusIcon className="w-4 h-4" aria-hidden="true" />}
                        onClick={() => { setEditingTask(null); setFormOpen(true); }}
                    >
                        Nueva tarea
                    </Button>
                )}

                {loaded && (
                    <TaskFilterTabs
                        tabs={filters}
                        activeTab={filter}
                        onTabChange={setFilter}
                        ariaLabel="Filtrar tareas"
                    />
                )}
            </div>

            <div className="tasks-panel__list">
                {loading && !loaded ? (
                    <div className="tasks-panel__skeleton" aria-label="Cargando tareas">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="tasks-panel__skeleton-card">
                                <div className="h-4 bg-gray-200 rounded w-2/3 mb-2.5" />
                                <div className="h-3 bg-gray-100 rounded w-1/2 mb-1.5" />
                                <div className="h-3 bg-gray-100 rounded w-2/5" />
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="tasks-panel__error">
                        <p className="tasks-panel__empty-text mb-4">{error}</p>
                        <Button variant="outline" size="sm" onClick={() => dispatch(fetchCourseTasks(courseId))}>
                            Reintentar
                        </Button>
                    </div>
                ) : visibleTasks.length === 0 ? (
                    <div className="tasks-panel__empty">
                        <ClipboardDocumentListIcon className="tasks-panel__empty-icon" aria-hidden="true" />
                        <p className="tasks-panel__empty-text">
                            {EMPTY_MESSAGES[isTeacherViewRole(role) ? 'teacher' : (role || 'student')]?.[filter] || 'No hay tareas para mostrar.'}
                        </p>
                    </div>
                ) : (
                    <div className="tasks-panel__list-inner course-tab-stagger">
                        {visibleTasks.map((task) => (
                            <TaskCard
                                key={task._id}
                                task={task}
                                role={role}
                                onOpen={() => setDetailTaskId(task._id)}
                            />
                        ))}
                    </div>
                )}
            </div>

            <TaskFormDrawer
                isOpen={formOpen}
                onClose={closeForm}
                courseId={courseId}
                task={editingTask}
            />

            <TaskDetailDrawer
                isOpen={Boolean(detailTaskId)}
                onClose={() => setDetailTaskId(null)}
                taskId={detailTaskId}
                courseId={courseId}
                onEdit={handleEdit}
            />
        </div>
    );
};
